import { useCallback, lazy } from 'react'

import type {
  TypeHeaderBanner,
  TypeLandingPage,
  TypeNt_audience,
  TypeNt_experience,
  TypeSectionFooter,
  TypeSeo,
} from '@/types/ctf'

import { cx } from '@/utils/strings'
import NinetailedProviderWrapper from '@/components/ninetailed-provider.tsx'
import { mapNtContentfulAudiences, mapNtContentfulExperiences } from '@/utils/ninetailed.ts'

import colors from '@/constants/colors'
import PageWrapper from '@/components/page-wrapper'
import { isPrint } from '@/utils/print'
import { useLocalStorage, useDarkMode } from 'usehooks-ts'
import { usePage } from '@inertiajs/react'
import { buttonStyles } from '@/components/ui/cta-link'
import DownloadPdfButton from '@/components/ui/download-pdf-button'
import SectionLandingPageSections from '@/components/section/landing-page-sections'
import Routes from '@/constants/routes'
import PageBackground from '@/components/ui/page-background'

// Lazy load print-related components
const PrintWrapper = lazy(() => import('@/components/print-wrapper'))

export type LandingPageProps = {
  entry: TypeLandingPage<'WITHOUT_UNRESOLVABLE_LINKS'>
  allAudiences?: Array<any>
  allExperiences?: Array<any>
  jobs?: Array<any>
  seo: TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>
  headerNav: TypeHeaderBanner<'WITHOUT_UNRESOLVABLE_LINKS'>
  footerNav: TypeSectionFooter<'WITHOUT_UNRESOLVABLE_LINKS'>
  headerBanners: Array<TypeHeaderBanner<'WITHOUT_UNRESOLVABLE_LINKS'>>
  downloadAsPdf?: boolean
  newsletterForm: any
  ntAudiences: Array<TypeNt_audience<'WITHOUT_UNRESOLVABLE_LINKS'>>
  ntExperiences: Array<TypeNt_experience<'WITHOUT_UNRESOLVABLE_LINKS'>>
}

// declare dataLayer
declare global {
  interface Window {
    dataLayer: any[]
  }
}

export default function LandingPage({
  seo: defaultSeo,
  headerNav: defaultHeaderNav,
  footerNav,
  headerBanners,
  entry,
  jobs,
  newsletterForm,
  ntAudiences,
  ntExperiences,
}: LandingPageProps) {
  const { indigoBlue, primaryBlue, lightGray, white } = colors

  let printTitle = defaultSeo?.fields?.title
  let printDescription = defaultSeo?.fields?.description
  let printTagline = ''
  const { VITE_NINETAILED_CLIENT_ID, VITE_NINETAILED_CLIENT_ENV } = import.meta.env

  const isPrintView = isPrint()

  const { url } = usePage()

  const pathname = url.split('?')[0]

  const {
    fields: {
      downloadAsPdf,
      pageBackgroundImage,
      darkMode,
      backgroundColor = 'White',
      sections = [],
      seo,
      headerNav,
      showSearchFilter,
      landingPageSections,
    },
  } = entry

  if (
    sections?.length > 0 &&
    sections[0]?.sys?.contentType?.sys?.id === 'sectionSetOfCards' &&
    sections[0]?.fields.cards?.length === 1
  ) {
    printTitle = sections[0].fields.cards[0]?.fields.headline
    printDescription = sections[0].fields.cards[0]?.fields.subtext
    printTagline = sections[0].fields.cards[0]?.fields.tagline
  }
  const [theme, setThemeValue, removeThemeValue] = useLocalStorage('theme', null)

  const Wrapper = useCallback(
    ({ children }) =>
      isPrintView ? (
        <PrintWrapper
          coverImage="fingerprint"
          pageType="landingPage"
          title={printTitle}
          description={printDescription}
          tagline={printTagline}
          pageUrl={Routes.hostname + pathname}
          orientation="portrait"
        >
          <NinetailedProviderWrapper
            allExperiences={mapNtContentfulExperiences(ntExperiences)}
            allAudiences={mapNtContentfulAudiences(ntAudiences)}
            ninetailedClientId={VITE_NINETAILED_CLIENT_ID}
            ninetailedEnv={VITE_NINETAILED_CLIENT_ENV}
          >
            {children}
          </NinetailedProviderWrapper>
        </PrintWrapper>
      ) : (
        <NinetailedProviderWrapper
          allExperiences={mapNtContentfulExperiences(ntExperiences)}
          allAudiences={mapNtContentfulAudiences(ntAudiences)}
          ninetailedClientId={VITE_NINETAILED_CLIENT_ID}
          ninetailedEnv={VITE_NINETAILED_CLIENT_ENV}
        >
          <PageWrapper
            bodyTw="mb-12 relative"
            {...{
              seo: {
                ...defaultSeo,
                ...seo,
              },
              headerBanners,
              headerNav: headerNav || defaultHeaderNav,
              footerNav,
              darkMode,
              newsletterForm,
              canExperiment: true,
            }}
          >
            {children}
            {downloadAsPdf && (
              <section className="max-w-8xl mx-auto px-5 lg:px-9">
                <div className="text-center px-5 py-6">
                  <DownloadPdfButton
                    btnText="Get page details as PDF"
                    backgroundColor={colors.white}
                    buttonStyle={buttonStyles.btnOutlined}
                  />
                </div>
              </section>
            )}
          </PageWrapper>
        </NinetailedProviderWrapper>
      ),
    [isPrintView]
  )

  return (
    <div
      className={cx(
        isPrintView ? 'bg-white' : 'dark:bg-secondary-900 bg-secondary-50',
        backgroundColor === indigoBlue && `bg-indigoBlue`,
        backgroundColor === primaryBlue && `bg-primaryBlue`,
        backgroundColor === lightGray && `bg-secondary-50`
      )}
    >
      {pageBackgroundImage && (
        <PageBackground pageBackgroundImage={pageBackgroundImage} backgroundColor={backgroundColor} />
      )}

      <Wrapper>
        {/* landing page sections is a component that houses all the landing page components so we can set up a ninetailed a/b test on the page as a whole */}
        {landingPageSections && (
          <SectionLandingPageSections
            key={`landing-page-section-${landingPageSections.sys.id}`}
            id={landingPageSections.sys.id}
            showSearchFilter={showSearchFilter}
            sections={landingPageSections.fields.sections}
            sectionsBackgroundImage={landingPageSections.fields.sectionsBackgroundImage}
            experiences={landingPageSections.fields.nt_experiences}
            backgroundColor={backgroundColor}
            jobs={jobs}
          />
        )}
        {sections.length >= 1 && (
          <SectionLandingPageSections
            showSearchFilter={showSearchFilter}
            sections={sections}
            backgroundColor={backgroundColor}
            jobs={jobs}
          />
        )}
      </Wrapper>
    </div>
  )
}
